import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import {
  Notification,
  NotificationGroup,
} from "@progress/kendo-react-notification";
import { useCatMgrs, type CatMgrRow } from "src/dm/hooks/useLookups";
import { useClearItemCache } from "src/dm/hooks/useItemCache";

// Legacy Admin/ManageItemCache.aspx — picks a cat-mgr + banner, posts to
// POST /admin/item-cache/clear, surfaces a transient success/failure toast.
//
// Banner list: legacy populates from Banner.GetByAllowDealsFlag(true) — that
// shipping path isn't exposed on the v1 API, so we hardcode the two
// banner codes the API + SPA actually use (GE / MD). If/when the v1
// /lookups/banners endpoint lands, replace `bannerOptions` with a `useQuery`.
interface BannerRow {
  code: string;
  label: string;
}

const BANNER_OPTIONS: BannerRow[] = [
  { code: "GE", label: "GE (Giant Eagle)" },
  { code: "MD", label: "MD (Market District)" },
];

const CAT_MGR_PLACEHOLDER: CatMgrRow = {
  code: "",
  name: "— Select a Category Manager —",
};
const BANNER_PLACEHOLDER: BannerRow = {
  code: "",
  label: "— Select a Banner —",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  padding: "1rem 1.25rem",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const ManageItemCache = () => {
  const catMgrs = useCatMgrs();
  const clearCache = useClearItemCache();

  const [catMgr, setCatMgr] = useState<CatMgrRow>(CAT_MGR_PLACEHOLDER);
  const [banner, setBanner] = useState<BannerRow>(BANNER_PLACEHOLDER);
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);

  // Auto-dismiss the confirm toast — same pattern as DealLayout's Term Sheet
  // toggle. Error toasts stay a bit longer so the operator can read them.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.type === "error" ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const catMgrData = useMemo<CatMgrRow[]>(
    () => [CAT_MGR_PLACEHOLDER, ...(catMgrs.data ?? [])],
    [catMgrs.data]
  );

  const canSubmit = !!catMgr.code && !!banner.code && !clearCache.isPending;

  const handleClick = async () => {
    setToast(null);
    try {
      const result = await clearCache.mutateAsync({
        catMgrCode: Number(catMgr.code),
        banner: banner.code,
      });
      setToast({
        type: "success",
        text: result.message || `Cache cleared for Cat Mgr ${catMgr.code} / ${banner.code}.`,
      });
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number; data?: { message?: string; detail?: string } } }).response;
      const msg =
        r?.data?.detail ??
        r?.data?.message ??
        (err instanceof Error ? err.message : "unknown error");
      setToast({
        type: "error",
        text: `Cache clear failed: ${msg}`,
      });
    }
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{ fontSize: "0.85rem", color: "#0a3d62", textDecoration: "none" }}
      >
        ← Admin
      </Link>
      <div>
        <h1 style={{ margin: 0 }}>Manage Item Cache</h1>
        <p style={{ color: "#475569", marginTop: "0.25rem" }}>
          Invalidates the item-lookup cache that Item Search reads from.
          Legacy backed this with an in-process per-(cat-mgr × banner) cache;
          the v1 API equivalent is a refresh of <code>RP_ITEM_AUTH_BANNERS_MV</code>.
        </p>
      </div>

      <section style={cardStyle}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.75rem 1rem",
          }}
        >
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Category Manager
            </span>
            <DropDownList
              data={catMgrData}
              textField="name"
              dataItemKey="code"
              value={catMgr}
              loading={catMgrs.isLoading}
              onChange={(e: DropDownListChangeEvent) =>
                setCatMgr(e.value ?? CAT_MGR_PLACEHOLDER)
              }
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Banner
            </span>
            <DropDownList
              data={[BANNER_PLACEHOLDER, ...BANNER_OPTIONS]}
              textField="label"
              dataItemKey="code"
              value={banner}
              onChange={(e: DropDownListChangeEvent) =>
                setBanner((e.value as BannerRow) ?? BANNER_PLACEHOLDER)
              }
            />
          </label>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1rem",
            gap: "0.5rem",
          }}
        >
          <Button
            themeColor="primary"
            disabled={!canSubmit}
            onClick={handleClick}
          >
            {clearCache.isPending ? "Clearing…" : "Clear Cache"}
          </Button>
        </div>

        {catMgrs.isError && (
          <p style={{ color: "#b91c1c", fontSize: "0.8rem", marginTop: "0.5rem" }}>
            Failed to load category managers:{" "}
            {catMgrs.error instanceof Error
              ? catMgrs.error.message
              : "unknown"}
          </p>
        )}
      </section>

      <NotificationGroup
        style={{
          position: "fixed",
          right: "1rem",
          bottom: "1rem",
          zIndex: 10000,
        }}
      >
        {toast && (
          <Notification
            type={{ style: toast.type, icon: true }}
            closable
            onClose={() => setToast(null)}
          >
            <span>{toast.text}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
};

export default ManageItemCache;
